import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import axios from 'axios'
import { useHistory, useParams } from 'react-router-dom'

import { fetchImage, fetchPointImage } from '../../../api/buildingFetches'
import {
  Job,
  getJobsByViewpoint,
  listJobsForProjectTrackerFloor,
} from '../../../api/jobs'
import {
  ProgressAiPointResult,
  ReviewQueueMeta,
  ReviewQueuePoint,
  ReviewViewpointStl,
  getReviewTaskQueue,
  getReviewTaskViewpointStls,
  listReviewTaskAiBatchPoints,
  heartbeatReviewTask,
  postReviewTaskActivityLog,
  postReviewTaskObservations,
  postReviewTaskVisit,
} from '../../../api/reviewTasks'
import { FloorProgressTranslation, Point } from '../../../api/types'
import icon3dBlue from '../../../assets/images/icon_3d_blue.svg'
import icon3dWhite from '../../../assets/images/icon_3d_white.svg'
import iconBack from '../../../assets/images/icon_back.svg'
import iconHistory from '../../../assets/images/icon_history.svg'
import { useBuildingContext } from '../../../contexts/buildingContext'
import { useNotifications } from '../../../contexts/notificationProvider'
import { PannellumViewersProvider } from '../../../contexts/pannellumContext'
import { useGeneratedPaths } from '../../../hooks/useGeneratedPaths'
import { createObjectURLSafe } from '../../../utils/imageUtils'
import {
  getCadHeight,
  getPrimaryToSecondary,
} from '../../../utils/progressFloorTransform'
import { LoadingIndicator } from '../../common/LoadingIndicator'
import { FullscreenPanoramaBackButton } from '../image_viewer/styles'
import { ViewerPosition } from '../image_viewer/types'

import { ProgressAiSuggestion, aiSuggestionJobKey } from './ProgressAiSuggestionCard'
import { ReviewExamplesSheet } from './ReviewExamplesSheet'
import { ReviewMinimap } from './ReviewMinimap'
import { ReviewPanoPane } from './ReviewPanoPane'
import {
  AiSuggestionNavStop,
  aiSuggestionStopProgress,
  buildAiSuggestionNavStops,
  filterQueueToSuggestionPoints,
  findAiSuggestionStopIndex,
  firstRemainingQueuePoint,
  floorNameFromProject,
  formatAiSuggestionNavLabel,
  formatViewpointNavLabel,
  isPendingReviewJob,
  suggestionViewpointPkSet,
} from './reviewTaskFormat'
import { ReviewTrackingPanel } from './ReviewTrackingPanel'
import {
  ActionButton,
  CancelButton,
  ConfirmCard,
  ConfirmOverlay,
  EmptyPane,
  HelpIconButton,
  HistoryControls,
  HistoryIconButton,
  HistoryToolbarRow,
  NavIconButton,
  OpacityStepButton,
  OpacityStrip,
  PanoRoot,
  PanoStage,
  ReviewRailSaveColumn,
  ReviewRailSplit,
  ReviewRailTrackingColumn,
  ReviewRightRail,
  SaveBarColumn,
  SaveCluster,
  SaveNavGroup,
  SavePrimary,
  SaveSecondary,
  SaveStack,
  SaveVpCell,
  SaveVpLabel,
  SaveVpRow,
  StlOffsetControls,
  StlOffsetLabel,
  StlOffsetNudge,
  StlOffsetReset,
  StlOffsetValue,
  StlOverlayToggleButton,
  StubWrap,
} from './styles'
import { examplesForJobTypes } from './trackerExamples'
import { usePendingReviewTaskCount } from './usePendingReviewTaskCount'
import { useReviewSessionTime } from './useReviewSessionTime'
import { useReviewTaskClaim } from './useReviewTaskClaim'

interface LoadedPane {
  imageUrl: string
  angle: number
  takenOn: string | null
}

interface CachedPano extends LoadedPane {
  imageId: number
}

const defaultPosition: ViewerPosition = {
  yaw: 0,
  pitch: 0,
  hfov: 120,
}

/** Prior-scan opacity steps; 100% = old image only. */
const OVERLAY_OPACITY_STEPS = [0.2, 0.4, 0.6, 0.8, 1] as const
const DEFAULT_OVERLAY_OPACITY = OVERLAY_OPACITY_STEPS[2]

type TrackerDraftStash = {
  selectedIds: number[]
  baselineInstalledIds: number[]
}

const selectionDiffersFromBaseline = (
  selectedIds: number[],
  baselineInstalledIds: number[],
): boolean => {
  const selected = new Set(selectedIds)
  if (selected.size !== baselineInstalledIds.length) {
    return true
  }
  return baselineInstalledIds.some(id => !selected.has(id))
}

const viewerPositionFromSearch = (search: string): ViewerPosition | null => {
  const params = new URLSearchParams(search)
  const yaw = params.get('yaw')
  const pitch = params.get('pitch')
  if (yaw == null || pitch == null) {
    return null
  }
  const parsedYaw = parseFloat(yaw)
  const parsedPitch = parseFloat(pitch)
  if (!Number.isFinite(parsedYaw) || !Number.isFinite(parsedPitch)) {
    return null
  }
  const hfovParam = params.get('hfov')
  const parsedHfov =
    hfovParam != null ? parseFloat(hfovParam) : defaultPosition.hfov
  return {
    yaw: parsedYaw,
    pitch: parsedPitch,
    hfov: Number.isFinite(parsedHfov) ? parsedHfov : defaultPosition.hfov,
  }
}

type ReviewNavIntent = {
  direction: 'forward' | 'back'
  source: 'button' | 'keyboard' | 'minimap' | 'auto'
  trackerId?: number | null
}

const ReviewTaskWorkspaceInner = () => {
  const history = useHistory()
  const { taskId, floorId, pointId } = useParams<{
    taskId: string
    floorId?: string
    pointId?: string
  }>()
  const {
    generateReviewOverviewPath,
    generateReviewTaskFloorPath,
    generateReviewTaskPointPath,
  } = useGeneratedPaths()
  const { addNotification } = useNotifications()
  const { state: buildingState } = useBuildingContext()
  const { adjustPendingReviewTaskCount } = usePendingReviewTaskCount()
  const projectId = buildingState.projectId as string

  const [queue, setQueue] = useState<ReviewQueuePoint[]>([])
  const [meta, setMeta] = useState<ReviewQueueMeta | null>(null)
  const [loadingQueue, setLoadingQueue] = useState(true)
  const [queueError, setQueueError] = useState<null | 'meta' | 'points'>(null)
  const queueRequestRef = useRef(0)
  const [loadingPanos, setLoadingPanos] = useState(false)
  const [busy, setBusy] = useState(false)
  const [latest, setLatest] = useState<LoadedPane | null>(null)
  const [previous, setPrevious] = useState<LoadedPane | null>(null)
  const [historyEnabled, setHistoryEnabled] = useState(false)
  const [overlayOpacity, setOverlayOpacity] = useState<number>(
    DEFAULT_OVERLAY_OPACITY,
  )
  const [overlayOffset, setOverlayOffset] = useState<ViewerPosition>({
    yaw: 0,
    pitch: 0,
    hfov: 0,
  })
  const [previousReady, setPreviousReady] = useState(false)
  const cameraInitializedRef = useRef(false)
  const [viewpointJobs, setViewpointJobs] = useState<Job[]>([])
  const [floorJobs, setFloorJobs] = useState<Job[]>([])
  const [loadingViewpointJobs, setLoadingViewpointJobs] = useState(false)
  const [selectedJobIds, setSelectedJobIds] = useState<number[]>([])
  const [draftByTracker, setDraftByTracker] = useState<
    Record<number, TrackerDraftStash>
  >({})
  const [hiddenJobTypeIds, setHiddenJobTypeIds] = useState<number[]>([])
  const [selectedTrackerId, setSelectedTrackerId] = useState<number | null>(
    null,
  )
  const [hoverItemId, setHoverItemId] = useState<number | null>(null)
  const [aiPointResults, setAiPointResults] = useState<ProgressAiPointResult[]>(
    [],
  )
  const [aiAssistPointsReady, setAiAssistPointsReady] = useState(false)
  const [examplesOpen, setExamplesOpen] = useState(false)
  const [stlOverlayEnabled, setStlOverlayEnabled] = useState(false)
  const [stlOffset, setStlOffset] = useState({ x: 0, y: 0 })
  const [viewpointStls, setViewpointStls] = useState<ReviewViewpointStl[]>([])
  const [stlFetchError, setStlFetchError] = useState<string | null>(null)
  const [viewerPosition, setViewerPosition] =
    useState<ViewerPosition>(defaultPosition)
  const [pendingIndex, setPendingIndex] = useState<number | null>(null)
  const [pendingTrackerId, setPendingTrackerId] = useState<number | null>(null)
  const cacheRef = useRef<Record<number, CachedPano>>({})
  const draftByTrackerRef = useRef(draftByTracker)
  draftByTrackerRef.current = draftByTracker
  const selectedJobIdsRef = useRef(selectedJobIds)
  selectedJobIdsRef.current = selectedJobIds
  const viewpointJobsRef = useRef(viewpointJobs)
  viewpointJobsRef.current = viewpointJobs

  const {
    claimToken,
    claimedActiveSeconds,
    setSecondsProvider,
  } = useReviewTaskClaim(projectId, taskId, {
    disabled: meta?.status === 'completed',
  })

  const { sessionSeconds, getAccumulatedSeconds, flushSessionTime, pauseSessionTime } =
    useReviewSessionTime(projectId, taskId, 'active', {
      disabled: meta?.status === 'completed',
      initialSeconds: claimedActiveSeconds || meta?.active_seconds,
      onFlush: seconds => {
        if (!claimToken || !projectId || !taskId) {
          return
        }
        void heartbeatReviewTask(projectId, taskId, {
          claim_token: claimToken,
          accumulated_seconds: seconds,
        }).catch(() => undefined)
      },
    })

  useEffect(() => {
    setSecondsProvider(getAccumulatedSeconds)
  }, [getAccumulatedSeconds, setSecondsProvider])

  const currentIndex = useMemo(() => {
    if (!pointId) {
      return -1
    }
    return queue.findIndex(
      entry =>
        String(entry.point_id) === String(pointId) &&
        entry.floor_code === floorId,
    )
  }, [floorId, pointId, queue])

  const current = currentIndex >= 0 ? queue[currentIndex] : null
  const currentPointId = current?.point_id
  const currentFloorCode = current?.floor_code
  const currentViewpointPk = current?.viewpoint_pk
  const currentLatestTakenOn = current?.latest_taken_on

  const suggestionViewpointPks = useMemo(
    () => suggestionViewpointPkSet(aiPointResults),
    [aiPointResults],
  )
  const aiAssistNavActive = Boolean(meta?.ai_assist && aiAssistPointsReady)
  const suggestionStops = useMemo(
    () =>
      aiAssistNavActive
        ? buildAiSuggestionNavStops(
            queue,
            meta?.trackers || [],
            aiPointResults,
          )
        : [],
    [aiAssistNavActive, aiPointResults, meta?.trackers, queue],
  )
  const reviewQueue = useMemo(
    () =>
      aiAssistNavActive
        ? filterQueueToSuggestionPoints(queue, suggestionViewpointPks)
        : queue,
    [aiAssistNavActive, queue, suggestionViewpointPks],
  )
  const trackerOrder = useMemo(
    () => (meta?.trackers || []).map(tracker => tracker.id),
    [meta?.trackers],
  )
  const currentSuggestionStopIndex = useMemo(() => {
    if (!aiAssistNavActive || suggestionStops.length === 0 || currentIndex < 0) {
      return -1
    }
    return findAiSuggestionStopIndex(
      suggestionStops,
      currentIndex,
      selectedTrackerId,
      trackerOrder,
    )
  }, [
    aiAssistNavActive,
    currentIndex,
    selectedTrackerId,
    suggestionStops,
    trackerOrder,
  ])
  const currentSuggestionStop =
    currentSuggestionStopIndex >= 0
      ? suggestionStops[currentSuggestionStopIndex]
      : null
  const prevSuggestionStop =
    currentSuggestionStopIndex > 0
      ? suggestionStops[currentSuggestionStopIndex - 1]
      : null
  const nextSuggestionStop =
    currentSuggestionStopIndex >= 0 &&
    currentSuggestionStopIndex < suggestionStops.length - 1
      ? suggestionStops[currentSuggestionStopIndex + 1]
      : currentSuggestionStopIndex < 0 && suggestionStops.length > 0
        ? suggestionStops[0]
        : null
  const suggestionProgress = useMemo(
    () => aiSuggestionStopProgress(suggestionStops, queue),
    [queue, suggestionStops],
  )

  const prevViewpointLabel = useMemo(() => {
    if (aiAssistNavActive) {
      return formatAiSuggestionNavLabel(prevSuggestionStop, suggestionStops)
    }
    return currentIndex > 0
      ? formatViewpointNavLabel(queue[currentIndex - 1])
      : ''
  }, [
    aiAssistNavActive,
    currentIndex,
    prevSuggestionStop,
    queue,
    suggestionStops,
  ])
  const currentViewpointLabel = useMemo(() => {
    if (aiAssistNavActive) {
      return formatAiSuggestionNavLabel(currentSuggestionStop, suggestionStops)
    }
    return formatViewpointNavLabel(current)
  }, [aiAssistNavActive, current, currentSuggestionStop, suggestionStops])
  const nextViewpointLabel = useMemo(() => {
    if (aiAssistNavActive) {
      return formatAiSuggestionNavLabel(nextSuggestionStop, suggestionStops)
    }
    return currentIndex >= 0 && currentIndex < queue.length - 1
      ? formatViewpointNavLabel(queue[currentIndex + 1])
      : ''
  }, [
    aiAssistNavActive,
    currentIndex,
    nextSuggestionStop,
    queue,
    suggestionStops,
  ])
  const selectedTrackerName = useMemo(() => {
    if (selectedTrackerId == null) {
      return null
    }
    return (
      meta?.trackers.find(tracker => tracker.id === selectedTrackerId)?.name ??
      null
    )
  }, [meta?.trackers, selectedTrackerId])
  const dirty = useMemo(() => {
    const selected = new Set(selectedJobIds)
    return viewpointJobs.some(job => {
      const installed = !isPendingReviewJob(job)
      return selected.has(job.id) !== installed
    })
  }, [selectedJobIds, viewpointJobs])

  const logUiActivity = useCallback(
    (
      eventType:
        | 'ui_stl_overlay_toggled'
        | 'ui_previous_image_overlay_toggled',
      metadata: Record<string, unknown>,
    ) => {
      if (!projectId || !taskId || currentViewpointPk == null) {
        return
      }
      void postReviewTaskActivityLog(projectId, taskId, {
        viewpoint_pk: currentViewpointPk,
        events: [{ event_type: eventType, metadata }],
      }).catch(() => {
        // Non-blocking: review UX should not fail if logging is unavailable.
      })
    },
    [currentViewpointPk, projectId, taskId],
  )

  const navIntentRef = useRef<ReviewNavIntent | null>(null)
  const seenPointKeyRef = useRef<string | null>(null)
  const logPointNavigated = useCallback(
    (
      entry: ReviewQueuePoint,
      intent: ReviewNavIntent,
      trackerId?: number | null,
    ) => {
      if (!projectId || !taskId) {
        return
      }
      const resolvedTrackerId = trackerId ?? intent.trackerId
      void postReviewTaskActivityLog(projectId, taskId, {
        viewpoint_pk: entry.viewpoint_pk,
        events: [
          {
            event_type: 'point_navigated',
            metadata: {
              direction: intent.direction,
              source: intent.source,
            },
            ...(resolvedTrackerId != null
              ? { project_tracker_id: resolvedTrackerId }
              : {}),
          },
        ],
      }).catch(() => {
        // Non-blocking: review UX should not fail if logging is unavailable.
      })
    },
    [projectId, taskId],
  )

  useEffect(() => {
    if (!current) {
      return
    }
    const key = `${current.floor_code}:${current.point_id}`
    if (seenPointKeyRef.current == null) {
      seenPointKeyRef.current = key
      navIntentRef.current = null
      return
    }
    if (seenPointKeyRef.current === key) {
      return
    }
    seenPointKeyRef.current = key
    const intent = navIntentRef.current
    navIntentRef.current = null
    if (!intent) {
      return
    }
    logPointNavigated(current, intent, intent.trackerId)
  }, [current, logPointNavigated])

  const hasUnsavedChanges = useMemo(() => {
    if (dirty) {
      return true
    }
    return Object.entries(draftByTracker).some(([trackerId, stash]) => {
      if (
        selectedTrackerId != null &&
        Number(trackerId) === selectedTrackerId
      ) {
        return false
      }
      return selectionDiffersFromBaseline(
        stash.selectedIds,
        stash.baselineInstalledIds,
      )
    })
  }, [dirty, draftByTracker, selectedTrackerId])

  const goToIndex = useCallback(
    (index: number, replace = true) => {
      flushSessionTime()
      const entry = queue[index]
      if (!entry || !projectId) {
        return
      }
      const destinationKey = `${entry.floor_code}:${entry.point_id}`
      if (seenPointKeyRef.current === destinationKey) {
        navIntentRef.current = null
      }
      const path = generateReviewTaskPointPath(
        projectId,
        taskId,
        entry.floor_code,
        String(entry.point_id),
      )
      const params = new URLSearchParams()
      params.set('yaw', viewerPosition.yaw.toFixed(3))
      params.set('pitch', viewerPosition.pitch.toFixed(3))
      params.set('hfov', viewerPosition.hfov.toFixed(3))
      const next = `${path}?${params.toString()}`
      if (replace) {
        history.replace(next)
      } else {
        history.push(next)
      }
    },
    [
      flushSessionTime,
      generateReviewTaskPointPath,
      history,
      projectId,
      queue,
      taskId,
      viewerPosition.hfov,
      viewerPosition.pitch,
      viewerPosition.yaw,
    ],
  )

  const syncCameraToUrl = useCallback(
    (position: ViewerPosition) => {
      if (!pointId) {
        return
      }
      const params = new URLSearchParams(window.location.search)
      params.set('yaw', position.yaw.toFixed(3))
      params.set('pitch', position.pitch.toFixed(3))
      params.set('hfov', position.hfov.toFixed(3))
      const nextSearch = `?${params.toString()}`
      if (nextSearch === window.location.search) {
        return
      }
      history.replace(`${window.location.pathname}${nextSearch}`)
    },
    [history, pointId],
  )

  const cameraUrlTimerRef = useRef<number | null>(null)
  const onViewerPosition = useCallback(
    (position: ViewerPosition) => {
      setViewerPosition(position)
      if (cameraUrlTimerRef.current != null) {
        window.clearTimeout(cameraUrlTimerRef.current)
      }
      cameraUrlTimerRef.current = window.setTimeout(() => {
        syncCameraToUrl(position)
      }, 150)
    },
    [syncCameraToUrl],
  )

  useEffect(() => {
    return () => {
      if (cameraUrlTimerRef.current != null) {
        window.clearTimeout(cameraUrlTimerRef.current)
      }
    }
  }, [])

  const pendingAiTrackerIdRef = useRef<number | null>(null)
  const lastAutoTrackerPointRef = useRef<number | null>(null)

  const clearPendingNavigation = useCallback(() => {
    setPendingIndex(null)
    setPendingTrackerId(null)
  }, [])

  const requestGoToIndex = useCallback(
    (index: number, trackerId?: number | null) => {
      if (hasUnsavedChanges) {
        setPendingIndex(index)
        setPendingTrackerId(trackerId ?? null)
        return
      }
      if (trackerId != null) {
        pendingAiTrackerIdRef.current = trackerId
      } else {
        pendingAiTrackerIdRef.current = null
        lastAutoTrackerPointRef.current = null
      }
      goToIndex(index)
    },
    [goToIndex, hasUnsavedChanges],
  )

  const goToSuggestionStop = useCallback(
    (stop: { queueIndex: number; trackerId: number }) => {
      if (stop.queueIndex === currentIndex) {
        setSelectedTrackerId(stop.trackerId)
        const intent = navIntentRef.current
        navIntentRef.current = null
        if (intent && current) {
          logPointNavigated(current, intent, stop.trackerId)
        }
        return
      }
      requestGoToIndex(stop.queueIndex, stop.trackerId)
    },
    [current, currentIndex, logPointNavigated, requestGoToIndex],
  )

  const loadQueue = useCallback(async () => {
    if (!projectId || !taskId) {
      return
    }
    const requestId = queueRequestRef.current + 1
    queueRequestRef.current = requestId
    const stillCurrent = () => queueRequestRef.current === requestId
    setLoadingQueue(true)
    setQueueError(null)
    try {
      const metaResponse = await getReviewTaskQueue(projectId, taskId, {
        metaOnly: true,
      })
      if (!stillCurrent()) {
        return
      }
      setMeta(metaResponse.meta)
    } catch {
      if (!stillCurrent()) {
        return
      }
      setQueueError('meta')
      setLoadingQueue(false)
      addNotification('Unable to load review queue', 'error')
      return
    }
    try {
      const response = await getReviewTaskQueue(projectId, taskId)
      if (!stillCurrent()) {
        return
      }
      setQueue(response.data || [])
      setMeta(response.meta)
    } catch {
      if (!stillCurrent()) {
        return
      }
      setQueue([])
      setQueueError('points')
      addNotification('Unable to load review queue', 'error')
    } finally {
      if (stillCurrent()) {
        setLoadingQueue(false)
      }
    }
  }, [addNotification, projectId, taskId])

  useEffect(() => {
    loadQueue()
  }, [loadQueue])

  useEffect(() => {
    if (!projectId || !taskId || !meta?.ai_assist) {
      setAiPointResults([])
      setAiAssistPointsReady(false)
      return
    }
    let cancelled = false
    setAiAssistPointsReady(false)
    listReviewTaskAiBatchPoints(projectId, Number(taskId))
      .then(result => {
        if (!cancelled) {
          setAiPointResults(result.points || [])
          setAiAssistPointsReady(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAiPointResults([])
          setAiAssistPointsReady(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [meta?.ai_assist, projectId, taskId])

  // If AI-assist filter is on and the current URL point has no suggestions,
  // jump to the nearest suggestion stop.
  useEffect(() => {
    if (!aiAssistNavActive || currentIndex < 0 || !suggestionStops.length) {
      return
    }
    if (!suggestionViewpointPks.has(queue[currentIndex]?.viewpoint_pk)) {
      const nearest =
        suggestionStops.find(stop => stop.queueIndex >= currentIndex) ||
        suggestionStops[suggestionStops.length - 1]
      pendingAiTrackerIdRef.current = nearest.trackerId
      navIntentRef.current = {
        direction: 'forward',
        source: 'auto',
        trackerId: nearest.trackerId,
      }
      goToIndex(nearest.queueIndex)
    }
  }, [
    aiAssistNavActive,
    currentIndex,
    goToIndex,
    queue,
    suggestionStops,
    suggestionViewpointPks,
  ])

  const aiSuggestionsByJobKey = useMemo(() => {
    const map: Record<string, ProgressAiSuggestion[]> = {}
    if (currentViewpointPk == null) {
      return map
    }
    const point = aiPointResults.find(
      entry =>
        entry.viewpoints_point_id === currentViewpointPk &&
        entry.status === 'succeeded',
    )
    for (const suggestion of point?.suggestions || []) {
      const itemId = Number(suggestion.item_id)
      if (!Number.isFinite(itemId)) continue
      const key = aiSuggestionJobKey(itemId, suggestion.job_type || '')
      if (!map[key]) map[key] = []
      map[key].push(suggestion)
    }
    return map
  }, [aiPointResults, currentViewpointPk])

  useEffect(() => {
    if (loadingQueue || !queue.length || currentIndex >= 0) {
      return
    }
    if (aiAssistNavActive && suggestionStops.length > 0) {
      const first = suggestionStops[0]
      pendingAiTrackerIdRef.current = first.trackerId
      navIntentRef.current = {
        direction: 'forward',
        source: 'auto',
        trackerId: first.trackerId,
      }
      goToIndex(first.queueIndex)
      return
    }
    const next = firstRemainingQueuePoint(reviewQueue)
    if (!next) {
      return
    }
    const index = queue.findIndex(
      entry => entry.viewpoint_pk === next.viewpoint_pk,
    )
    if (index >= 0) {
      navIntentRef.current = { direction: 'forward', source: 'auto' }
      goToIndex(index)
    }
  }, [
    aiAssistNavActive,
    currentIndex,
    goToIndex,
    loadingQueue,
    queue,
    reviewQueue,
    suggestionStops,
  ])

  const loadPano = useCallback(
    async (
      imageId: number | null,
      floorCode: string,
    ): Promise<LoadedPane | null> => {
      if (!imageId || !projectId) {
        return null
      }
      const cached = cacheRef.current[imageId]
      if (cached) {
        return cached
      }
      const viewpointsImage = await fetchPointImage(
        projectId,
        floorCode,
        String(imageId),
      )
      if (!viewpointsImage?.processed_image_url) {
        return null
      }
      const blob = await fetchImage(viewpointsImage.processed_image_url)
      const imageUrl = createObjectURLSafe(blob)
      if (!imageUrl) {
        return null
      }
      const loaded: CachedPano = {
        imageId,
        imageUrl,
        angle: viewpointsImage.angle || 0,
        takenOn: viewpointsImage.taken_on || null,
      }
      cacheRef.current[imageId] = loaded
      return loaded
    },
    [projectId],
  )

  useEffect(() => {
    const cache = cacheRef.current
    return () => {
      Object.values(cache).forEach(entry => {
        URL.revokeObjectURL(entry.imageUrl)
      })
    }
  }, [])

  useEffect(() => {
    setSelectedJobIds([])
    setDraftByTracker({})
    setHiddenJobTypeIds([])
    setPendingIndex(null)
    setHistoryEnabled(false)
    setOverlayOffset({ yaw: 0, pitch: 0, hfov: 0 })
    setStlOffset({ x: 0, y: 0 })
    setStlOverlayEnabled(false)
  }, [currentViewpointPk])

  useEffect(() => {
    if (
      currentViewpointPk == null ||
      !projectId ||
      !currentFloorCode ||
      current?.latest_image_id == null
    ) {
      return
    }
    let cancelled = false
    setLoadingPanos(true)
    setLatest(null)
    setPrevious(null)
    setPreviousReady(false)
    if (!cameraInitializedRef.current) {
      // Read search once on first mount — do not depend on location.search
      // (camera pan writes yaw/pitch/hfov into the URL and would re-trigger load).
      setViewerPosition(
        viewerPositionFromSearch(window.location.search) || defaultPosition,
      )
      cameraInitializedRef.current = true
    }
    const latestImageId = current.latest_image_id
    const previousImageId = current.previous_image_id
    const floorCode = currentFloorCode
    const run = async () => {
      try {
        const [latestPane, previousPane] = await Promise.all([
          loadPano(latestImageId, floorCode),
          loadPano(previousImageId, floorCode),
        ])
        if (!cancelled) {
          setLatest(latestPane)
          setPrevious(previousPane)
        }
      } catch {
        if (!cancelled) {
          addNotification('Unable to load 360 images', 'error')
        }
      } finally {
        if (!cancelled) {
          setLoadingPanos(false)
        }
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [
    addNotification,
    current?.latest_image_id,
    current?.previous_image_id,
    currentFloorCode,
    currentViewpointPk,
    loadPano,
    projectId,
  ])

  // Prefetch next point panos without resetting selection / overlay state.
  useEffect(() => {
    if (!projectId || currentIndex < 0) {
      return
    }
    const next = queue[currentIndex + 1]
    if (!next) {
      return
    }
    void loadPano(next.latest_image_id, next.floor_code)
    void loadPano(next.previous_image_id, next.floor_code)
  }, [currentIndex, loadPano, projectId, queue])

  useEffect(() => {
    if (currentPointId == null || !projectId || !selectedTrackerName) {
      setViewpointJobs([])
      setLoadingViewpointJobs(false)
      return
    }
    const observationDate = currentLatestTakenOn
      ? new Date(`${currentLatestTakenOn}T12:00:00`)
      : null
    const cancelSource = axios.CancelToken.source()
    const trackerIdForLoad = selectedTrackerId
    let cancelled = false
    setLoadingViewpointJobs(true)
    setViewpointJobs([])
    getJobsByViewpoint(
      projectId,
      selectedTrackerName,
      currentPointId,
      observationDate,
      false,
      cancelSource,
      true,
    )
      .then(response => {
        if (cancelled) {
          return
        }
        const all: Job[] = response.data || []
        setViewpointJobs(all)
        const baselineInstalledIds = all
          .filter(job => !isPendingReviewJob(job))
          .map(job => job.id)
        const stash =
          trackerIdForLoad != null
            ? draftByTrackerRef.current[trackerIdForLoad]
            : undefined
        if (stash) {
          setSelectedJobIds(stash.selectedIds)
        } else {
          setSelectedJobIds(baselineInstalledIds)
        }
      })
      .catch(err => {
        if (axios.isCancel(err) || cancelled) {
          return
        }
        setViewpointJobs([])
        addNotification('Unable to load items for this viewpoint', 'error')
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingViewpointJobs(false)
        }
      })
    return () => {
      cancelled = true
      cancelSource.cancel('viewpoint changed')
    }
  }, [
    addNotification,
    currentLatestTakenOn,
    currentPointId,
    projectId,
    selectedTrackerId,
    selectedTrackerName,
  ])

  useEffect(() => {
    if (currentViewpointPk == null || !projectId || !taskId) {
      setViewpointStls([])
      setStlFetchError(null)
      return
    }
    let cancelled = false
    setStlFetchError(null)
    getReviewTaskViewpointStls(projectId, taskId, currentViewpointPk)
      .then(meshes => {
        if (cancelled) {
          return
        }
        setViewpointStls(meshes)
        if (!meshes.length) {
          setStlFetchError(null)
        }
      })
      .catch(err => {
        if (cancelled) {
          return
        }
        setViewpointStls([])
        const status = err?.response?.status
        const detail =
          err?.response?.data?.error?.details?.[0]?.message ||
          err?.response?.data?.meta?.message ||
          err?.message
        setStlFetchError(
          status
            ? `Failed to load STLs (${status}${detail ? `: ${detail}` : ''})`
            : 'Failed to load STLs for this viewpoint',
        )
      })
    return () => {
      cancelled = true
    }
  }, [currentViewpointPk, projectId, taskId])

  // Floor jobs are for examples and job-type visibility — refetch only when
  // floor or selected tracker changes, not on every viewpoint (was causing 502s).
  useEffect(() => {
    if (!currentFloorCode || !projectId || !selectedTrackerName) {
      setFloorJobs([])
      return
    }
    const cancelSource = axios.CancelToken.source()
    let cancelled = false
    setFloorJobs([])
    listJobsForProjectTrackerFloor(
      projectId,
      selectedTrackerName,
      currentFloorCode,
      null,
      cancelSource,
      false,
      true,
    )
      .then(jobs => {
        if (cancelled) {
          return
        }
        setFloorJobs(jobs)
      })
      .catch(err => {
        if (axios.isCancel(err) || cancelled) {
          return
        }
        setFloorJobs([])
        addNotification('Unable to load floor tracker data', 'error')
      })
    return () => {
      cancelled = true
      cancelSource.cancel('floor or tracker changed')
    }
  }, [addNotification, currentFloorCode, projectId, selectedTrackerName])
  const [showInstalled, setShowInstalled] = useState(false)

  const visibleViewpointJobs = useMemo(
    () =>
      showInstalled ? viewpointJobs : viewpointJobs.filter(isPendingReviewJob),
    [showInstalled, viewpointJobs],
  )

  const visibleFloorJobs = useMemo(
    () => (showInstalled ? floorJobs : floorJobs.filter(isPendingReviewJob)),
    [floorJobs, showInstalled],
  )

  useEffect(() => {
    const trackers = meta?.trackers || []
    if (!trackers.length) {
      setSelectedTrackerId(null)
      return
    }
    if (
      selectedTrackerId == null ||
      !trackers.some(tracker => tracker.id === selectedTrackerId)
    ) {
      if (aiAssistNavActive && currentIndex >= 0) {
        const firstStop = suggestionStops.find(
          stop => stop.queueIndex === currentIndex,
        )
        setSelectedTrackerId(firstStop?.trackerId ?? trackers[0].id)
        return
      }
      setSelectedTrackerId(trackers[0].id)
    }
  }, [
    aiAssistNavActive,
    currentIndex,
    meta?.trackers,
    selectedTrackerId,
    suggestionStops,
  ])

  // On arriving at a point during AI-assist review, land on the first tracker
  // with suggestions (or a pending tracker from Next/Prev).
  useEffect(() => {
    if (!aiAssistNavActive || currentIndex < 0) {
      return
    }
    const pendingTrackerIdFromNav = pendingAiTrackerIdRef.current
    if (pendingTrackerIdFromNav != null) {
      setSelectedTrackerId(pendingTrackerIdFromNav)
      pendingAiTrackerIdRef.current = null
      lastAutoTrackerPointRef.current = currentIndex
      return
    }
    if (lastAutoTrackerPointRef.current === currentIndex) {
      return
    }
    const firstStop = suggestionStops.find(
      stop => stop.queueIndex === currentIndex,
    )
    if (firstStop) {
      setSelectedTrackerId(firstStop.trackerId)
    }
    lastAutoTrackerPointRef.current = currentIndex
  }, [aiAssistNavActive, currentIndex, suggestionStops])

  const trackerFloorJobs = useMemo(() => {
    if (selectedTrackerId == null) {
      return visibleFloorJobs
    }
    return visibleFloorJobs.filter(
      job => job.item.project_tracker === selectedTrackerId,
    )
  }, [selectedTrackerId, visibleFloorJobs])

  const trackerFloorMapJobs = useMemo(() => {
    if (selectedTrackerId == null) {
      return floorJobs
    }
    return floorJobs.filter(
      job => job.item.project_tracker === selectedTrackerId,
    )
  }, [floorJobs, selectedTrackerId])

  const matchedExampleGroups = useMemo(() => {
    const jobsForTracker =
      selectedTrackerId == null
        ? floorJobs
        : floorJobs.filter(
            job => job.item.project_tracker === selectedTrackerId,
          )
    const byKey = new Map<string, { name: string; slug?: string }>()
    jobsForTracker.forEach(job => {
      const jobType = job.type.job_type
      const key = jobType.slug || String(jobType.id)
      if (!byKey.has(key)) {
        byKey.set(key, { name: jobType.name, slug: jobType.slug })
      }
    })
    return examplesForJobTypes(Array.from(byKey.values()))
  }, [floorJobs, selectedTrackerId])

  const visibleJobTypeIds = useMemo(() => {
    const floorTypeIds = Array.from(
      new Set(trackerFloorJobs.map(job => job.type.job_type.id)),
    )
    const viewpointTypeIds = Array.from(
      new Set(
        visibleViewpointJobs
          .filter(
            job =>
              selectedTrackerId == null ||
              job.item.project_tracker === selectedTrackerId,
          )
          .map(job => job.type.job_type.id),
      ),
    )
    const baseTypeIds =
      floorTypeIds.length > 0 ? floorTypeIds : viewpointTypeIds
    if (!hiddenJobTypeIds.length) {
      return baseTypeIds
    }
    const hidden = new Set(hiddenJobTypeIds)
    return baseTypeIds.filter(id => !hidden.has(id))
  }, [
    hiddenJobTypeIds,
    selectedTrackerId,
    trackerFloorJobs,
    visibleViewpointJobs,
  ])

  const selectedItemIds = useMemo(() => {
    const ids = new Set<number>()
    visibleViewpointJobs
      .filter(job => selectedJobIds.includes(job.id))
      .forEach(job => ids.add(job.item.id))
    if (hoverItemId != null) {
      ids.add(hoverItemId)
    }
    return Array.from(ids)
  }, [hoverItemId, selectedJobIds, visibleViewpointJobs])

  const onSelectItemIds = (itemIds: number[], mode: 'toggle' | 'add') => {
    const matchingJobs = trackerFloorJobs.filter(
      job =>
        itemIds.includes(job.item.id) &&
        visibleViewpointJobs.some(viewpointJob => viewpointJob.id === job.id),
    )
    const matchingIds = matchingJobs.map(job => job.id)
    setSelectedJobIds(currentIds => {
      if (mode === 'add') {
        return Array.from(new Set([...currentIds, ...matchingIds]))
      }
      const selected = new Set(currentIds)
      matchingIds.forEach(id => {
        if (selected.has(id)) {
          selected.delete(id)
        } else {
          selected.add(id)
        }
      })
      return Array.from(selected)
    })
  }

  const applyVisitResult = useCallback(
    (
      action: 'confirmed' | 'skipped',
      result: {
        status: ReviewQueueMeta['status']
        points_viewed: number
        points_total: number
        task_changes_made: number
        completed: boolean
      },
    ) => {
      if (!current) {
        return result.completed
      }
      setQueue(entries =>
        entries.map(entry =>
          entry.viewpoint_pk === current.viewpoint_pk
            ? { ...entry, visited_action: action }
            : entry,
        ),
      )
      setMeta(currentMeta =>
        currentMeta
          ? {
              ...currentMeta,
              status: result.status,
              points_viewed: result.points_viewed,
              points_total: result.points_total,
              changes_made: result.task_changes_made,
            }
          : currentMeta,
      )
      if (result.completed) {
        adjustPendingReviewTaskCount(-1)
        history.push(generateReviewOverviewPath(projectId))
        return true
      }
      return false
    },
    [
      adjustPendingReviewTaskCount,
      current,
      generateReviewOverviewPath,
      history,
      projectId,
    ],
  )

  const goBackToFloor = useCallback(() => {
    pauseSessionTime()
    if (!projectId) {
      return
    }
    const code = current?.floor_code || floorId
    if (code) {
      history.push(generateReviewTaskFloorPath(projectId, taskId, code))
      return
    }
    history.push(generateReviewOverviewPath(projectId))
  }, [
    current?.floor_code,
    floorId,
    generateReviewOverviewPath,
    generateReviewTaskFloorPath,
    history,
    pauseSessionTime,
    projectId,
    taskId,
  ])

  const finishVisit = useCallback(
    async (
      action: 'confirmed' | 'skipped',
      changesMade = 0,
      advance = action === 'skipped',
    ) => {
      if (!current) {
        return false
      }
      setBusy(true)
      try {
        const result = await postReviewTaskVisit(projectId, taskId, {
          viewpoint_pk: current.viewpoint_pk,
          action,
          changes_made: changesMade,
        })
        const completed = applyVisitResult(action, result)
        if (completed) {
          pauseSessionTime()
          return true
        }
        const scopedQueue = aiAssistNavActive ? reviewQueue : queue
        const allVisited = scopedQueue.every(
          entry =>
            entry.viewpoint_pk === current.viewpoint_pk ||
            Boolean(entry.visited_action),
        )
        let nextIndex =
          pendingIndex != null ? pendingIndex : currentIndex + 1
        let nextTrackerId = pendingTrackerId
        if (pendingIndex == null && aiAssistNavActive) {
          let nextStop: AiSuggestionNavStop | null =
            suggestionStops[
              findAiSuggestionStopIndex(
                suggestionStops,
                currentIndex,
                selectedTrackerId,
                trackerOrder,
              ) + 1
            ] ?? null
          // Skip leaves the whole viewpoint — jump to the next point's
          // first suggestion tracker, not the next tracker on this point.
          if (action === 'skipped') {
            nextStop =
              suggestionStops.find(stop => stop.queueIndex > currentIndex) ??
              null
          }
          if (!nextStop) {
            nextIndex = queue.length
            nextTrackerId = null
          } else {
            nextIndex = nextStop.queueIndex
            nextTrackerId = nextStop.trackerId
          }
        }
        const hasNext = nextIndex >= 0 && nextIndex < queue.length
        if (allVisited || (advance && !hasNext)) {
          clearPendingNavigation()
          goBackToFloor()
          return false
        }
        if (advance) {
          clearPendingNavigation()
          if (nextTrackerId != null) {
            pendingAiTrackerIdRef.current = nextTrackerId
          }
          if (nextIndex === currentIndex && nextTrackerId != null) {
            setSelectedTrackerId(nextTrackerId)
            const intent = navIntentRef.current
            navIntentRef.current = null
            if (intent && current) {
              logPointNavigated(current, intent, nextTrackerId)
            }
          } else {
            goToIndex(nextIndex)
          }
        } else {
          clearPendingNavigation()
        }
        flushSessionTime()
        return false
      } catch {
        addNotification('Unable to save visit', 'error')
        return false
      } finally {
        setBusy(false)
      }
    },
    [
      addNotification,
      aiAssistNavActive,
      applyVisitResult,
      clearPendingNavigation,
      current,
      currentIndex,
      flushSessionTime,
      goBackToFloor,
      goToIndex,
      logPointNavigated,
      pauseSessionTime,
      pendingIndex,
      pendingTrackerId,
      projectId,
      queue,
      reviewQueue,
      selectedTrackerId,
      suggestionStops,
      taskId,
      trackerOrder,
    ],
  )

  const saveMarks = useCallback(
    async (advanceAfter = false) => {
      if (!current?.latest_image_id) {
        addNotification('This viewpoint has no latest scan to mark', 'error')
        return
      }
      const selected = new Set(selectedJobIds)
      const toInstall = viewpointJobs.filter(
        job => selected.has(job.id) && isPendingReviewJob(job),
      )
      const toUninstall = viewpointJobs.filter(
        job => !selected.has(job.id) && !isPendingReviewJob(job),
      )
      if (!toInstall.length && !toUninstall.length) {
        return
      }
      setBusy(true)
      try {
        let written = 0
        if (toInstall.length) {
          const result = await postReviewTaskObservations(projectId, taskId, {
            image: current.latest_image_id,
            job_ids: toInstall.map(job => job.id),
            observed_quantity: 1,
          })
          written += result.written
        }
        if (toUninstall.length) {
          const result = await postReviewTaskObservations(projectId, taskId, {
            image: current.latest_image_id,
            job_ids: toUninstall.map(job => job.id),
            observed_quantity: 0,
          })
          written += result.written
        }
        const installIds = new Set(toInstall.map(job => job.id))
        const uninstallIds = new Set(toUninstall.map(job => job.id))
        const applyLocal = (jobs: Job[]) =>
          jobs.map(job => {
            if (installIds.has(job.id)) {
              const total = Number(job.total_units)
              return {
                ...job,
                completed_units:
                  Number.isFinite(total) && total > 0 ? total : 1,
              }
            }
            if (uninstallIds.has(job.id)) {
              return { ...job, completed_units: 0 }
            }
            return job
          })
        const nextViewpointJobs = applyLocal(viewpointJobs)
        setViewpointJobs(nextViewpointJobs)
        setFloorJobs(applyLocal)
        setSelectedJobIds(
          nextViewpointJobs
            .filter(job => !isPendingReviewJob(job))
            .map(job => job.id),
        )
        if (selectedTrackerId != null) {
          setDraftByTracker(currentDrafts => {
            if (!(selectedTrackerId in currentDrafts)) {
              return currentDrafts
            }
            const next = { ...currentDrafts }
            delete next[selectedTrackerId]
            return next
          })
        }
        await finishVisit('confirmed', written, advanceAfter)
        flushSessionTime()
      } catch {
        addNotification('Unable to save progress marks', 'error')
        setBusy(false)
      }
    },
    [
      addNotification,
      current,
      finishVisit,
      flushSessionTime,
      projectId,
      selectedJobIds,
      selectedTrackerId,
      taskId,
      viewpointJobs,
    ],
  )

  const finishVisitRef = useRef(finishVisit)
  finishVisitRef.current = finishVisit
  const requestGoToIndexRef = useRef(requestGoToIndex)
  requestGoToIndexRef.current = requestGoToIndex

  const requestAdvance = useCallback((source: 'button' | 'keyboard' = 'button') => {
    if (!current || currentIndex < 0) {
      return
    }
    if (aiAssistNavActive) {
      if (!nextSuggestionStop) {
        return
      }
      navIntentRef.current = {
        direction: 'forward',
        source,
        trackerId: nextSuggestionStop.trackerId,
      }
      if (hasUnsavedChanges) {
        setPendingIndex(nextSuggestionStop.queueIndex)
        setPendingTrackerId(nextSuggestionStop.trackerId)
        return
      }
      if (current.visited_action || nextSuggestionStop.queueIndex === currentIndex) {
        goToSuggestionStop(nextSuggestionStop)
        return
      }
      void finishVisit('confirmed', 0, true)
      return
    }
    if (currentIndex >= queue.length - 1) {
      return
    }
    const nextIndex = currentIndex + 1
    navIntentRef.current = { direction: 'forward', source }
    if (hasUnsavedChanges) {
      setPendingIndex(nextIndex)
      setPendingTrackerId(null)
      return
    }
    if (current.visited_action) {
      goToIndex(nextIndex)
      return
    }
    void finishVisit('confirmed', 0, true)
  }, [
    aiAssistNavActive,
    current,
    currentIndex,
    finishVisit,
    goToIndex,
    goToSuggestionStop,
    hasUnsavedChanges,
    nextSuggestionStop,
    queue.length,
  ])

  const requestRetreat = useCallback((source: 'button' | 'keyboard' = 'button') => {
    if (aiAssistNavActive) {
      if (!prevSuggestionStop) {
        return
      }
      navIntentRef.current = {
        direction: 'back',
        source,
        trackerId: prevSuggestionStop.trackerId,
      }
      goToSuggestionStop(prevSuggestionStop)
      return
    }
    if (currentIndex > 0) {
      navIntentRef.current = { direction: 'back', source }
      requestGoToIndex(currentIndex - 1)
    }
  }, [
    aiAssistNavActive,
    currentIndex,
    goToSuggestionStop,
    prevSuggestionStop,
    requestGoToIndex,
  ])

  const requestAdvanceRef = useRef(requestAdvance)
  requestAdvanceRef.current = requestAdvance
  const requestRetreatRef = useRef(requestRetreat)
  requestRetreatRef.current = requestRetreat
  const currentIndexRef = useRef(currentIndex)
  currentIndexRef.current = currentIndex
  const queueLengthRef = useRef(queue.length)
  queueLengthRef.current = queue.length

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (document.querySelector('[data-review-confirm]')) {
        if (event.key === 'Escape') {
          navIntentRef.current = null
          clearPendingNavigation()
        }
        return
      }
      const target = event.target as HTMLElement
      if (target.closest('input, select, textarea, button')) {
        return
      }
      if (event.key === 'Escape') {
        return
      }
      if (event.key === 'ArrowLeft' || event.key === 'j') {
        requestRetreatRef.current('keyboard')
      }
      if (event.key === 'ArrowRight' || event.key === 'k') {
        requestAdvanceRef.current('keyboard')
      }
      if (event.key === 's' || event.key === 'S') {
        void finishVisitRef.current('skipped')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [clearPendingNavigation])

  const floorName = floorNameFromProject(
    buildingState.projectData?.floors,
    current?.floor_code || floorId || '',
  )

  const mapPoints = useMemo(() => {
    const floorQueue = reviewQueue.filter(
      entry => entry.floor_code === (current?.floor_code || floorId),
    )
    const byId = new Map(
      floorQueue.map(entry => [String(entry.point_id), entry]),
    )
    return ((buildingState.floorData?.points || []) as Point[])
      .filter(point => byId.has(String(point.point_id)))
      .map(point => ({
        ...point,
        severityClass: byId.get(String(point.point_id))?.visited_action
          ? 'back_green'
          : undefined,
      }))
  }, [
    buildingState.floorData?.points,
    current?.floor_code,
    floorId,
    reviewQueue,
  ])

  const progressTranslation = useMemo((): FloorProgressTranslation | null => {
    const floorCode = current?.floor_code || floorId
    if (!floorCode) {
      return null
    }
    return (
      buildingState.projectData?.progress_translation_json?.[floorCode] ?? null
    )
  }, [
    buildingState.projectData?.progress_translation_json,
    current?.floor_code,
    floorId,
  ])

  const currentPlanPoint = useMemo(() => {
    if (!current) {
      return null
    }
    const point =
      ((buildingState.floorData?.points || []) as Point[]).find(
        candidate => String(candidate.point_id) === String(current.point_id),
      ) ?? null
    if (!point) {
      return null
    }
    // API DecimalFields often serialize as strings; coerce for finite checks / affine math.
    const x = Number(point.x)
    const y = Number(point.y)
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return null
    }
    return { ...point, x, y }
  }, [buildingState.floorData?.points, current])

  const overlayStlMeshes = useMemo(() => {
    const visibleJobTypeSet = new Set(visibleJobTypeIds)
    const viewpointJobById = new Map(
      viewpointJobs.map(job => [job.id, job] as const),
    )
    const itemVisible = (itemId: number) => {
      const job = viewpointJobs.find(entry => entry.item.id === itemId)
      return job != null && visibleJobTypeSet.has(job.type.job_type.id)
    }

    // Item IDs from all viewpoint jobs (including installed), not only the
    // visible checklist rows — so installed STLs stay available when selected.
    const selectedItemIdSet = new Set<number>()
    viewpointJobs.forEach(job => {
      if (
        selectedJobIds.includes(job.id) &&
        visibleJobTypeSet.has(job.type.job_type.id)
      ) {
        selectedItemIdSet.add(job.item.id)
      }
    })
    if (hoverItemId != null && itemVisible(hoverItemId)) {
      selectedItemIdSet.add(hoverItemId)
    }

    return viewpointStls.filter(mesh => {
      if (
        selectedTrackerId != null &&
        mesh.project_tracker !== selectedTrackerId
      ) {
        return false
      }
      const job = viewpointJobById.get(mesh.job_id)
      if (!job || !visibleJobTypeSet.has(job.type.job_type.id)) {
        return false
      }
      return selectedItemIdSet.has(mesh.item_id)
    })
  }, [
    hoverItemId,
    selectedJobIds,
    selectedTrackerId,
    viewpointJobs,
    viewpointStls,
    visibleJobTypeIds,
  ])

  const trackerHasViewpointStls = useMemo(() => {
    if (selectedTrackerId == null) {
      return viewpointStls.length > 0
    }
    return viewpointStls.some(
      mesh => mesh.project_tracker === selectedTrackerId,
    )
  }, [selectedTrackerId, viewpointStls])

  const stlOverlayReady = useMemo(() => {
    if (!progressTranslation) {
      return false
    }
    if (!getPrimaryToSecondary(progressTranslation)) {
      return false
    }
    if (getCadHeight(progressTranslation) == null) {
      return false
    }
    if (currentPlanPoint == null) {
      return false
    }
    // Ready when STLs exist for this tracker/viewpoint; selection only toggles
    // which meshes draw (installed stay visible when their checklist rows are on).
    return trackerHasViewpointStls
  }, [currentPlanPoint, progressTranslation, trackerHasViewpointStls])

  const stlOverlayDisabledReason = useMemo(() => {
    if (stlFetchError) {
      return stlFetchError
    }
    if (!progressTranslation || !getPrimaryToSecondary(progressTranslation)) {
      return 'Floor progress translation is not configured'
    }
    if (getCadHeight(progressTranslation) == null) {
      return 'Floor cad_height is not configured'
    }
    if (currentPlanPoint == null) {
      return 'Viewpoint plan position is missing'
    }
    if (!trackerHasViewpointStls) {
      return viewpointStls.length
        ? 'No STL models for the selected tracker at this viewpoint'
        : 'No STL models available for items at this viewpoint'
    }
    return null
  }, [
    currentPlanPoint,
    progressTranslation,
    stlFetchError,
    trackerHasViewpointStls,
    viewpointStls.length,
  ])

  useEffect(() => {
    if (!stlOverlayReady && stlOverlayEnabled) {
      setStlOverlayEnabled(false)
    }
  }, [stlOverlayEnabled, stlOverlayReady])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (document.querySelector('[data-review-confirm], [data-review-idle]')) {
        return
      }
      const target = event.target as HTMLElement
      if (target.closest('input, select, textarea, button')) {
        return
      }
      if (event.key === '3') {
        if (!stlOverlayReady) {
          return
        }
        event.preventDefault()
        const next = !stlOverlayEnabled
        logUiActivity('ui_stl_overlay_toggled', { enabled: next })
        if (next && historyEnabled) {
          logUiActivity('ui_previous_image_overlay_toggled', {
            enabled: false,
            reason: 'stl_overlay_enabled',
          })
          setHistoryEnabled(false)
        }
        setStlOverlayEnabled(next)
        return
      }
      if (event.key === 'h' || event.key === 'H') {
        if (!previous || !previousReady) {
          return
        }
        event.preventDefault()
        const next = !historyEnabled
        logUiActivity('ui_previous_image_overlay_toggled', {
          enabled: next,
          opacity: overlayOpacity,
        })
        if (next && stlOverlayEnabled) {
          logUiActivity('ui_stl_overlay_toggled', {
            enabled: false,
            reason: 'previous_image_overlay_enabled',
          })
          setStlOverlayEnabled(false)
        }
        setHistoryEnabled(next)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [
    historyEnabled,
    logUiActivity,
    overlayOpacity,
    previous,
    previousReady,
    stlOverlayEnabled,
    stlOverlayReady,
  ])

  const trackerShell = (loadingItems: boolean) => (
    <ReviewTrackingPanel
      embedded={false}
      trackers={meta?.trackers || []}
      jobs={[]}
      selectedJobIds={[]}
      viewed={meta?.points_viewed ?? 0}
      total={meta?.points_total ?? 0}
      sessionSeconds={sessionSeconds}
      selectedTrackerId={selectedTrackerId}
      onSelectedTrackerIdChange={setSelectedTrackerId}
      visibleJobTypeIds={[]}
      showInstalled={showInstalled}
      onShowInstalledChange={setShowInstalled}
      onJobTypeVisibilityChange={() => undefined}
      onToggleJob={() => undefined}
      onSelectJobs={() => undefined}
      loading={loadingItems}
    />
  )

  if (!meta && loadingQueue) {
    return <LoadingIndicator />
  }

  if (queueError === 'meta') {
    return (
      <StubWrap>
        <h1>Unable to load review queue.</h1>
        <ActionButton type="button" onClick={() => void loadQueue()}>
          Retry
        </ActionButton>
      </StubWrap>
    )
  }

  if (!loadingQueue && queueError === 'points') {
    return (
      <>
        <StubWrap>
          <h1>Unable to load review points.</h1>
          <ActionButton type="button" onClick={() => void loadQueue()}>
            Retry
          </ActionButton>
        </StubWrap>
        {trackerShell(false)}
      </>
    )
  }

  if (!loadingQueue && !queue.length) {
    return (
      <StubWrap>
        <h1>No remaining items to review.</h1>
      </StubWrap>
    )
  }

  if (!current) {
    return (
      <>
        <EmptyPane>
          <LoadingIndicator />
        </EmptyPane>
        {trackerShell(true)}
      </>
    )
  }

  return (
    <PanoRoot>
      <PanoStage>
        <ReviewPanoPane
          latest={latest}
          previous={previous}
          loading={loadingPanos}
          busy={busy}
          historyEnabled={historyEnabled}
          overlayOpacity={overlayOpacity}
          overlayOffset={overlayOffset}
          onOverlayOffsetChange={setOverlayOffset}
          viewerPosition={viewerPosition}
          onViewerPosition={onViewerPosition}
          onPreviousReadyChange={setPreviousReady}
          stlOverlayEnabled={stlOverlayEnabled}
          stlOffset={stlOffset}
          onStlOffsetChange={setStlOffset}
          stlMeshes={overlayStlMeshes}
          planX={currentPlanPoint?.x ?? null}
          planY={currentPlanPoint?.y ?? null}
          progressTranslation={progressTranslation}
        />
      </PanoStage>
      <HistoryControls>
        {historyEnabled && (
          <OpacityStrip>
            {OVERLAY_OPACITY_STEPS.map(step => (
              <OpacityStepButton
                key={step}
                type="button"
                $level={step}
                $active={overlayOpacity === step}
                aria-label={`Prior scan opacity ${Math.round(step * 100)}%`}
                onClick={() => setOverlayOpacity(step)}
              />
            ))}
          </OpacityStrip>
        )}
        {stlOverlayEnabled && (
          <StlOffsetControls>
            <StlOffsetLabel>
              X<StlOffsetValue>{stlOffset.x.toFixed(1)}</StlOffsetValue>
              <StlOffsetNudge
                type="button"
                aria-label="Decrease STL X offset"
                onClick={() =>
                  setStlOffset(current => ({ ...current, x: current.x - 0.1 }))
                }
              >
                −
              </StlOffsetNudge>
              <StlOffsetNudge
                type="button"
                aria-label="Increase STL X offset"
                onClick={() =>
                  setStlOffset(current => ({ ...current, x: current.x + 0.1 }))
                }
              >
                +
              </StlOffsetNudge>
            </StlOffsetLabel>
            <StlOffsetLabel>
              Y<StlOffsetValue>{stlOffset.y.toFixed(1)}</StlOffsetValue>
              <StlOffsetNudge
                type="button"
                aria-label="Decrease STL Y offset"
                onClick={() =>
                  setStlOffset(current => ({ ...current, y: current.y - 0.1 }))
                }
              >
                −
              </StlOffsetNudge>
              <StlOffsetNudge
                type="button"
                aria-label="Increase STL Y offset"
                onClick={() =>
                  setStlOffset(current => ({ ...current, y: current.y + 0.1 }))
                }
              >
                +
              </StlOffsetNudge>
            </StlOffsetLabel>
            <StlOffsetReset
              type="button"
              disabled={stlOffset.x === 0 && stlOffset.y === 0}
              title="Reset 3D overlay offset"
              aria-label="Reset 3D overlay offset"
              onClick={() => setStlOffset({ x: 0, y: 0 })}
            >
              Reset
            </StlOffsetReset>
          </StlOffsetControls>
        )}
        <HistoryToolbarRow>
          <StlOverlayToggleButton
            type="button"
            $active={stlOverlayEnabled}
            $disabled={!stlOverlayReady}
            disabled={!stlOverlayReady}
            aria-label={
              stlOverlayEnabled ? 'Hide 3D STL overlay' : 'Show 3D STL overlay'
            }
            title={
              stlOverlayDisabledReason
                ? stlOverlayDisabledReason
                : stlOverlayEnabled
                  ? 'Hide CAD overlay (3) · hold Control + drag to shift'
                  : 'Show CAD overlay on 360 view (3)'
            }
            onClick={() => {
              const next = !stlOverlayEnabled
              logUiActivity('ui_stl_overlay_toggled', { enabled: next })
              if (next && historyEnabled) {
                logUiActivity('ui_previous_image_overlay_toggled', {
                  enabled: false,
                  reason: 'stl_overlay_enabled',
                })
                setHistoryEnabled(false)
              }
              setStlOverlayEnabled(next)
            }}
          >
            <img src={stlOverlayEnabled ? icon3dWhite : icon3dBlue} alt="" />
          </StlOverlayToggleButton>
          <HistoryIconButton
            type="button"
            $active={historyEnabled}
            $disabled={!previous || !previousReady}
            disabled={!previous || !previousReady}
            aria-label={
              historyEnabled
                ? 'Hide prior scan overlay'
                : 'Show prior scan overlay'
            }
            title={
              !previous
                ? 'No prior scan at this viewpoint'
                : !previousReady
                  ? 'Loading prior scan…'
                  : historyEnabled
                    ? 'Hide prior scan (h) · hold Control to move prior only'
                    : 'Show prior scan (h)'
            }
            onClick={() => {
              const next = !historyEnabled
              logUiActivity('ui_previous_image_overlay_toggled', {
                enabled: next,
                opacity: overlayOpacity,
              })
              if (next && stlOverlayEnabled) {
                logUiActivity('ui_stl_overlay_toggled', {
                  enabled: false,
                  reason: 'previous_image_overlay_enabled',
                })
                setStlOverlayEnabled(false)
              }
              setHistoryEnabled(next)
            }}
          >
            <img src={iconHistory} alt="" />
          </HistoryIconButton>
          <HelpIconButton
            type="button"
            $toolbar
            disabled={matchedExampleGroups.length === 0}
            aria-label="Show tracker examples"
            title={
              matchedExampleGroups.length === 0
                ? 'No example images for this tracker yet'
                : 'Examples for current tracker'
            }
            onClick={() => setExamplesOpen(true)}
          >
            ?
          </HelpIconButton>
        </HistoryToolbarRow>
      </HistoryControls>
      <FullscreenPanoramaBackButton onClick={goBackToFloor}>
        <img src={iconBack} alt="Back" />
        <span>Back</span>
      </FullscreenPanoramaBackButton>
      <ReviewRightRail $aiAssist={aiAssistNavActive}>
        <ReviewMinimap
          embedded
          jobs={trackerFloorMapJobs}
          selectedItemIds={selectedItemIds}
          selectedJobTypeIds={visibleJobTypeIds}
          onSelectItemIds={onSelectItemIds}
          loading={loadingViewpointJobs}
          onHoverItemId={setHoverItemId}
          yaw={viewerPosition.yaw}
          hfov={viewerPosition.hfov}
          floorName={floorName}
          captureDate={latest?.takenOn || current.latest_taken_on}
          highlightedPointId={String(current.point_id)}
          points={mapPoints}
          onClickPoint={pointSubId => {
            const index = queue.findIndex(
              entry =>
                String(entry.point_id) === String(pointSubId) &&
                entry.floor_code === current.floor_code,
            )
            if (index >= 0 && index !== currentIndex) {
              navIntentRef.current = {
                direction: index > currentIndex ? 'forward' : 'back',
                source: 'minimap',
              }
              requestGoToIndex(index)
            }
          }}
          onDoubleClickPoint={pointSubId => {
            const index = queue.findIndex(
              entry =>
                String(entry.point_id) === String(pointSubId) &&
                entry.floor_code === current.floor_code,
            )
            if (index >= 0 && index !== currentIndex) {
              navIntentRef.current = {
                direction: index > currentIndex ? 'forward' : 'back',
                source: 'minimap',
              }
              requestGoToIndex(index)
            }
          }}
        />
        <ReviewRailSplit>
          <ReviewRailSaveColumn $aiAssist={aiAssistNavActive}>
            <SaveBarColumn>
              <SaveNavGroup>
                <SaveCluster $aiAssist={aiAssistNavActive}>
                  <NavIconButton
                    type="button"
                    $aiAssist={aiAssistNavActive}
                    disabled={
                      busy ||
                      (aiAssistNavActive
                        ? !prevSuggestionStop
                        : currentIndex <= 0)
                    }
                    onClick={() => requestRetreat()}
                  >
                    ‹
                  </NavIconButton>
                  <SaveStack $aiAssist={aiAssistNavActive}>
                    <SavePrimary
                      type="button"
                      disabled={busy || !dirty}
                      onClick={() => void saveMarks(false)}
                    >
                      Save
                    </SavePrimary>
                    <SaveSecondary
                      type="button"
                      disabled={busy}
                      onClick={() => void finishVisit('skipped')}
                    >
                      Skip
                    </SaveSecondary>
                  </SaveStack>
                  <NavIconButton
                    type="button"
                    $aiAssist={aiAssistNavActive}
                    disabled={
                      busy ||
                      (aiAssistNavActive
                        ? !nextSuggestionStop
                        : currentIndex >= queue.length - 1)
                    }
                    onClick={() => requestAdvance()}
                  >
                    ›
                  </NavIconButton>
                </SaveCluster>
                <SaveVpRow>
                  <SaveVpCell $width={aiAssistNavActive ? 68 : 44}>
                    {prevViewpointLabel ? (
                      <SaveVpLabel>{prevViewpointLabel}</SaveVpLabel>
                    ) : null}
                  </SaveVpCell>
                  <SaveVpCell $width={96}>
                    {currentViewpointLabel ? (
                      <SaveVpLabel $current>
                        {currentViewpointLabel}
                      </SaveVpLabel>
                    ) : null}
                  </SaveVpCell>
                  <SaveVpCell $width={aiAssistNavActive ? 68 : 44}>
                    {nextViewpointLabel ? (
                      <SaveVpLabel>{nextViewpointLabel}</SaveVpLabel>
                    ) : null}
                  </SaveVpCell>
                </SaveVpRow>
              </SaveNavGroup>
            </SaveBarColumn>
          </ReviewRailSaveColumn>
          <ReviewRailTrackingColumn>
            <ReviewTrackingPanel
              embedded
              trackers={meta?.trackers || []}
              jobs={visibleViewpointJobs}
              selectedJobIds={selectedJobIds}
              selectedTrackerId={selectedTrackerId}
              onSelectedTrackerIdChange={nextTrackerId => {
                if (
                  selectedTrackerId != null &&
                  selectedTrackerId !== nextTrackerId
                ) {
                  const baselineInstalledIds = viewpointJobsRef.current
                    .filter(job => !isPendingReviewJob(job))
                    .map(job => job.id)
                  setDraftByTracker(currentDrafts => ({
                    ...currentDrafts,
                    [selectedTrackerId]: {
                      selectedIds: selectedJobIdsRef.current,
                      baselineInstalledIds,
                    },
                  }))
                }
                setSelectedTrackerId(nextTrackerId)
              }}
              visibleJobTypeIds={visibleJobTypeIds}
              showInstalled={showInstalled}
              onShowInstalledChange={setShowInstalled}
              onJobTypeVisibilityChange={(typeId, visible) => {
                setHiddenJobTypeIds(current => {
                  if (visible) {
                    return current.filter(id => id !== typeId)
                  }
                  return current.includes(typeId)
                    ? current
                    : [...current, typeId]
                })
              }}
              viewed={
                aiAssistNavActive
                  ? suggestionProgress.viewed
                  : meta?.points_viewed ??
                    queue.filter(entry => entry.visited_action).length
              }
              total={
                aiAssistNavActive
                  ? suggestionProgress.total
                  : meta?.points_total ?? queue.length
              }
              sessionSeconds={sessionSeconds}
              onToggleJob={jobId =>
                setSelectedJobIds(currentIds =>
                  currentIds.includes(jobId)
                    ? currentIds.filter(id => id !== jobId)
                    : [...currentIds, jobId],
                )
              }
              onSelectJobs={(jobIds, selected) =>
                setSelectedJobIds(currentIds => {
                  if (selected) {
                    return Array.from(new Set([...currentIds, ...jobIds]))
                  }
                  return currentIds.filter(id => !jobIds.includes(id))
                })
              }
              onHoverItemId={setHoverItemId}
              hoveredItemId={hoverItemId}
              loading={loadingViewpointJobs}
              aiSuggestionsByJobKey={aiSuggestionsByJobKey}
            />
          </ReviewRailTrackingColumn>
        </ReviewRailSplit>
      </ReviewRightRail>
      <ReviewExamplesSheet
        open={examplesOpen}
        groups={matchedExampleGroups}
        onClose={() => setExamplesOpen(false)}
      />
      {pendingIndex != null && (
        <ConfirmOverlay data-review-confirm>
          <ConfirmCard>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Save current tracker changes before leaving this point?
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <ActionButton
                type="button"
                disabled={busy}
                onClick={() => {
                  void saveMarks(false).then(() => {
                    navIntentRef.current = null
                    clearPendingNavigation()
                  })
                }}
              >
                Save
              </ActionButton>
              <CancelButton
                type="button"
                disabled={busy}
                onClick={() => void finishVisit('skipped', 0, true)}
              >
                Skip
              </CancelButton>
              <CancelButton
                type="button"
                onClick={() => {
                  navIntentRef.current = null
                  clearPendingNavigation()
                }}
              >
                Cancel
              </CancelButton>
            </div>
          </ConfirmCard>
        </ConfirmOverlay>
      )}
    </PanoRoot>
  )
}

export const ReviewTaskWorkspace = () => {
  return (
    <PannellumViewersProvider>
      <ReviewTaskWorkspaceInner />
    </PannellumViewersProvider>
  )
}
